import type { ActivationRoutingDiagnosis } from './types';

import { ENDPOINT } from '~/constants/endpoints';

import { type UUID } from '~/types/common';

import { isAbortError, type KyRequestOptions } from '@vestigas/data';

import { vestigasApi } from '~/services/kyClient';

import { log } from '~/utils';

import { zodActivationRoutingDiagnosisSchema } from './validations';

/**
 * Fetches why this supplier's (company's) activation emails go to the
 * supplier or to Customer Success, judged independently per document type
 * (delivery notes, invoices). Evaluates the same predicates as the send
 * path. The customer account flag that can override every condition
 * (`supplier_overview_force_cs_routing`) is not included: it belongs to the
 * requesting customer, not this supplier.
 *
 * @param companyId - The ID of the company (supplier) to diagnose.
 * @param options - Optional Ky request options including signal for aborting the request.
 * @returns The activation routing diagnosis.
 *
 * @see https://app.dev.vestigas.com/redoc#tag/VESTIGAS-Admin-Companies/operation/get_activation_routing_diagnosis_vadmin_company__company_id__activation_routing_diagnosis_get
 */
export const fetchActivationRoutingDiagnosis = async (
  companyId: UUID,
  options?: KyRequestOptions,
) => {
  try {
    return await vestigasApi
      .get(ENDPOINT.COMPANY.ACTIVATION_ROUTING_DIAGNOSIS.GET(companyId), {
        ...options,
        validate: {
          ...options?.validate,
          response: zodActivationRoutingDiagnosisSchema,
        },
      })
      .json<ActivationRoutingDiagnosis>();
  } catch (error) {
    if (isAbortError(error)) {
      // Re-throw AbortError without logging or toasting as it's expected when requests are cancelled
      throw error;
    }

    log.error({
      message: 'Error fetching activation routing diagnosis',
      error,
    });

    throw error;
  }
};
